import React, { useEffect, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register elements
Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface TournamentStats {
  totalTournaments: number;
  statusBreakdown: {
    scheduled?: number;
    locked?: number;
    ongoing?: number;
    completed?: number;
  };
  typeBreakdown: {
    normal?: number;
    death?: number;
  };
  totalParticipants: number;
  avgParticipants: number;
  completedMatches: number;
  recentTournaments: number;
  upcomingTournaments: number;
  activeTournaments: number;
}

const TournamentMetrics: React.FC = () => {
  const [tournamentStats, setTournamentStats] = useState<TournamentStats>({
    totalTournaments: 0,
    statusBreakdown: {},
    typeBreakdown: {},
    totalParticipants: 0,
    avgParticipants: 0,
    completedMatches: 0,
    recentTournaments: 0,
    upcomingTournaments: 0,
    activeTournaments: 0,
  });

  useEffect(() => {
    const fetchTournamentStats = async () => {
      try {
        const response = await axios.get('/api/admin/tournament-stats');
        setTournamentStats(response.data);
      } catch (error) {
        console.error('Error fetching tournament statistics', error);
      }
    };

    fetchTournamentStats();
  }, []);

  // Tournament status data
  const statusData = {
    labels: ['Scheduled', 'Locked', 'Ongoing', 'Completed'],
    datasets: [
      {
        label: 'Tournament Status',
        backgroundColor: ['#ffce56', '#ff6384', '#36a2eb', '#4bc0c0'],
        data: [
          tournamentStats.statusBreakdown.scheduled || 0,
          tournamentStats.statusBreakdown.locked || 0,
          tournamentStats.statusBreakdown.ongoing || 0,
          tournamentStats.statusBreakdown.completed || 0,
        ],
      },
    ],
  };

  // Tournament type data
  const typeData = {
    labels: ['Normal', 'Death'],
    datasets: [
      {
        data: [
          tournamentStats.typeBreakdown.normal || 0,
          tournamentStats.typeBreakdown.death || 0,
        ],
        backgroundColor: ['#00bfff', '#ff4500'],
      },
    ],
  };

  return (
    <>
      <h3>Tournament Statistics</h3>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Total</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.totalTournaments}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Active</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.activeTournaments}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Upcoming</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.upcomingTournaments}</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Participants</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.totalParticipants}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Avg/Tournament</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.avgParticipants}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Matches</h4>
            <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: '#f8f0c6' }}>{tournamentStats.completedMatches}</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', height: '250px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#ffd700', fontSize: '1rem', textAlign: 'center' }}>Tournament Status</h4>
          <div style={{ flex: 1 }}>
            <Bar
              data={statusData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#f8f0c6' },
                    grid: { color: '#6d4e27' }
                  },
                  x: {
                    ticks: { color: '#f8f0c6' },
                    grid: { color: '#6d4e27' }
                  }
                }
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#ffd700', fontSize: '1rem', textAlign: 'center' }}>Tournament Types</h4>
          <div style={{ flex: 1 }}>
            <Doughnut
              data={typeData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: '#f8f0c6', font: { size: 10 } }
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TournamentMetrics;
